import { useState } from "react";
import { Link } from "@/lib/router-compat";
import { motion, AnimatePresence } from "framer-motion";
import { List, LayoutGrid } from "lucide-react";
import type { IndustryOffering } from "@/data/industriesData";
import { getCategoryColor } from "@/lib/categoryColors";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { industryUI } from "@/i18n/industryDetail";

interface Props {
  offerings: IndustryOffering[];
  color?: string;
  sectionTitle?: string;
}

const IndustryOfferings = ({ offerings, color, sectionTitle }: Props) => {
  const lang = useLanguage();
  const ui = industryUI[lang] ?? industryUI.en;
  const heading = sectionTitle ?? ui.featured;
  const [layout, setLayout] = useState<"list" | "cards">("list");

  if (offerings.length === 0) return null;

  return (
    <section id="service-offerings" className="py-12 sm:py-16 scroll-mt-20">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        {/* Header row */}
        <div className="flex items-center gap-3 mb-2">
          <span className="w-6 h-0.5 rounded-full bg-primary" />
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Specialist project delivery", es: "Ejecución de proyectos", "zh-cn": "专业项目执行" }[lang])}</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">
            {heading}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {offerings.length}
            </span>
          </h2>

          {/* Layout toggle */}
          <div className="flex items-center border border-border/60 rounded-md overflow-hidden">
            <button
              onClick={() => setLayout("list")}
              className={`p-1.5 transition-colors ${layout === "list" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              aria-label="List view"
            >
              <List className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setLayout("cards")}
              className={`p-1.5 transition-colors ${layout === "cards" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              aria-label="Card view"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence mode="popLayout">
          {layout === "list" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {offerings.map((o, i) => {
                const catColor = o.category ? getCategoryColor(o.category) : null;
                return (
                <motion.div
                  key={o.title}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.02 }}
                >
                  {o.href ? (
                    <Link
                      to={localizeHref(o.href, lang)}
                      className="group flex items-center justify-between py-2.5 border-b border-border/40 hover:pl-1 transition-all"
                    >
                      <span className="flex items-center gap-2 text-sm text-foreground group-hover:text-primary transition-colors truncate pr-4">
                        {catColor && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: catColor.text }} />}
                        {o.title}
                      </span>
                      <span className="text-[10px] text-muted-foreground/50 group-hover:text-primary transition-colors shrink-0">
                        →
                      </span>
                    </Link>
                  ) : (
                    <div className="flex items-center justify-between py-2.5 border-b border-border/40">
                      <span className="flex items-center gap-2 text-sm text-foreground truncate pr-4">
                        {catColor && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: catColor.text }} />}
                        {o.title}
                      </span>
                    </div>
                  )}
                </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              key="cards"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {offerings.map((o, i) => {
                const catColor = o.category ? getCategoryColor(o.category) : null;
                const card = (
                  <motion.div
                    key={o.title}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.03 }}
                  >
                    <div
                      className="group block rounded-lg border border-border/50 overflow-hidden hover:border-border hover:shadow-sm transition-all h-full"
                      style={catColor ? { borderLeftWidth: "3px", borderLeftColor: catColor.text } : undefined}
                    >
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors mb-1">
                          {o.title}
                        </h3>
                        {o.description && (
                          <p className="text-xs text-muted-foreground line-clamp-4 leading-relaxed">
                            {o.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );

                return o.href ? (
                  <Link key={o.title} to={localizeHref(o.href, lang)} className="block">
                    {card}
                  </Link>
                ) : (
                  <div key={o.title}>{card}</div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default IndustryOfferings;
